import type {
  SoundAsset,
  SoundCatalog,
  SoundEventMapping,
  SoundLayer,
  SoundVariation,
} from "./types.js";

/**
 * `record[key]` only when `record` itself has `key`. Manifests are parsed JSON, so a plain lookup
 * would also find `Object.prototype` members (`toString`, `constructor`) and treat them as entries.
 */
export function ownValue<T>(record: Record<string, T>, key: string): T | undefined {
  return Object.hasOwn(record, key) ? record[key] : undefined;
}

/** An event's mapping as resolved for playback, with the assets its variations name. */
export interface ResolvedSoundEvent {
  /** The event that answered: the requested one, or a fallback. */
  id: string;
  layer: string;
  mapping: SoundEventMapping;
  assets: Record<string, SoundAsset>;
}

/**
 * Finds what an event sounds like. Each event in the fallback chain is looked up through every
 * layer before the next fallback is tried, so a tenant's own mapping beats a fallback's. A layer
 * answers whole (no field merge); one whose variations all name missing assets is skipped, so a
 * broken override never silences an event. An empty variation list is a deliberate silence.
 */
export function resolveSoundEvent(
  id: string,
  layers: readonly SoundLayer[],
  catalog: SoundCatalog,
): ResolvedSoundEvent | null {
  const seen = new Set<string>();
  let candidate: string | undefined = id;
  while (candidate && !seen.has(candidate)) {
    seen.add(candidate);
    for (const layer of layers) {
      const mapping = ownValue(layer.manifest.events, candidate);
      if (!mapping) continue;
      const { assets } = layer.manifest;
      const variations = mapping.variations.filter((v) => ownValue(assets, v.asset) != null);
      if (mapping.variations.length > 0 && variations.length === 0) continue;
      return {
        id: candidate,
        layer: layer.name,
        mapping: { ...mapping, variations },
        assets,
      };
    }
    candidate = ownValue(catalog, candidate)?.fallback;
  }
  return null;
}

/**
 * The asset a resolved variation plays. Resolution keeps only variations whose asset is in the
 * answering layer, so a miss means the manifest changed under a resolved event.
 */
export function requireAsset(resolved: ResolvedSoundEvent, variation: SoundVariation): SoundAsset {
  const asset = ownValue(resolved.assets, variation.asset);
  if (!asset) {
    throw new Error(`Sound asset "${variation.asset}" is missing from layer "${resolved.layer}"`);
  }
  return asset;
}
